import { Sheet, SheetContent } from "./ui/sheet";
import { Button } from "./ui/button";
import { BrandMark } from "./BrandMark";

/**
 * Shared mobile hamburger drawer for every public marketing page.
 *
 * Behavior enforced here (previously inconsistent per-page):
 *  - Renders through a portal (Radix Sheet) so it always sits above the page.
 *  - Solid 80 % backdrop — page content never bleeds through.
 *  - Locks body scroll while open (handled by Radix).
 *  - Closes on the X, on backdrop tap, on Escape, and on any nav-item tap.
 *  - Desktop nav is unaffected — this component is only mounted on
 *    `lg:hidden` widths by the parent header.
 *
 * Props:
 *  - open              : boolean
 *  - onOpenChange      : (open: boolean) => void
 *  - nav               : [{ label, to?, href? }] — same shape each page already uses
 *  - onNavItemClick    : (item) => void — page decides whether it's a route push or in-page scroll
 *  - onLoginClick?     : optional; renders a Customer Login CTA at the bottom when provided
 *  - loginLabel?       : string (defaults to "Customer Login")
 */
export function PublicMobileMenu({
    open,
    onOpenChange,
    nav = [],
    onNavItemClick,
    onLoginClick,
    loginLabel = "Customer Login",
    testId = "mobile-menu",
    activePath,
}) {
    const handle = (item) => {
        onOpenChange(false);
        // Defer so the sheet's close animation begins before any route change.
        setTimeout(() => onNavItemClick?.(item), 120);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                data-testid={testId}
                className="w-[85vw] max-w-sm sm:max-w-sm bg-white flex flex-col gap-3 p-5"
            >
                <div className="flex items-center">
                    <BrandMark size={32} />
                </div>
                <nav className="mt-2 flex flex-col gap-1">
                    {nav.map((n) => {
                        const isActive = activePath && n.to && n.to === activePath;
                        return (
                            <button
                                key={n.label}
                                type="button"
                                className={`text-left px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-medium tap ${
                                    isActive ? "text-brand-royal" : "text-slate-700"
                                }`}
                                onClick={() => handle(n)}
                                data-testid={`${testId}-item-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        );
                    })}
                </nav>
                {onLoginClick && (
                    <Button
                        className="mt-4 bg-brand-royal hover:bg-brand-hover rounded-full h-11"
                        onClick={() => {
                            onOpenChange(false);
                            setTimeout(() => onLoginClick(), 120);
                        }}
                        data-testid={`${testId}-login`}
                    >
                        {loginLabel}
                    </Button>
                )}
            </SheetContent>
        </Sheet>
    );
}
